import { useState, useEffect, useCallback } from "react";
import { API, axios } from "@/api";
import {
  Loader2, Check, X, Eye, AlertTriangle, Download,
  TrendingUp, Star, Shield, Clock,
} from "lucide-react";
import { toast } from "sonner";

const TABS = ["pending", "all", "type-intelligence"];

export default function AdminOffersPanel() {
  const [tab, setTab] = useState("pending");
  const [pending, setPending] = useState([]);
  const [allOffers, setAllOffers] = useState({ offers: [], total: 0 });
  const [typeIntel, setTypeIntel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");

  const fetchPending = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/birthday-offers/admin/pending`);
      setPending(res.data.offers || []);
    } catch { setPending([]); }
  }, []);

  const fetchAll = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/birthday-offers?page=${page}&limit=20`);
      setAllOffers(res.data);
    } catch { setAllOffers({ offers: [], total: 0 }); }
  }, [page]);

  const fetchTypeIntel = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/birthday-offers/admin/type-intelligence`);
      setTypeIntel(res.data);
    } catch { setTypeIntel(null); }
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      if (tab === "pending") await fetchPending();
      else if (tab === "all") await fetchAll();
      else await fetchTypeIntel();
      setLoading(false);
    })();
  }, [tab, page, fetchPending, fetchAll, fetchTypeIntel]);

  const reviewOffer = async (offerId, status, notes = "") => {
    try {
      await axios.put(`${API}/birthday-offers/admin/${offerId}/review`, { status, admin_review_notes: notes });
      toast.success(`Offer ${status}`);
      fetchPending();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Review failed");
    }
  };

  const stalenessColor = { fresh: "text-emerald-600", recent: "text-yellow-600", stale: "text-amber-600", unknown: "text-slate-400" };

  return (
    <div className="space-y-4" data-testid="admin-offers-panel">
      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
        {TABS.map(t => (
          <button key={t} onClick={() => { setTab(t); setPage(1); }}
            className={`flex-1 py-1.5 rounded-md text-xs font-semibold transition-colors ${tab === t ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
            data-testid={`offers-tab-${t}`}
          >{t === "pending" ? `Pending (${pending.length})` : t === "all" ? "All Offers" : "Type Intelligence"}</button>
        ))}
      </div>

      {loading && <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-amber-500" /></div>}

      {/* Pending Tab */}
      {!loading && tab === "pending" && (
        <div className="space-y-3" data-testid="offers-pending-list">
          {pending.length === 0 && <p className="text-sm text-slate-400 text-center py-4">No pending submissions</p>}
          {pending.map(o => (
            <div key={o.offer_id} className="bg-white rounded-xl border border-slate-200 p-4 space-y-2" data-testid={`pending-offer-${o.offer_id}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-bold text-slate-800">{o.offer_title}</p>
                  <p className="text-xs text-slate-500">{o.business_name} — by {o.submitter_name}</p>
                </div>
                <p className="text-[10px] text-slate-400">{o.created_at?.slice(0, 10)}</p>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-700">{o.offer_type}</span>
                {o.is_true_freebie && <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-700">True Freebie</span>}
              </div>
              <div className="flex gap-2">
                <button onClick={() => reviewOffer(o.offer_id, "approved")} className="flex-1 py-1.5 rounded-lg text-xs font-semibold bg-emerald-600 text-white hover:bg-emerald-700" data-testid={`approve-${o.offer_id}`}>
                  <Check className="w-3 h-3 inline mr-1" />Approve
                </button>
                <button onClick={() => reviewOffer(o.offer_id, "rejected")} className="flex-1 py-1.5 rounded-lg text-xs font-semibold bg-red-500 text-white hover:bg-red-600" data-testid={`reject-${o.offer_id}`}>
                  <X className="w-3 h-3 inline mr-1" />Reject
                </button>
                <button onClick={() => reviewOffer(o.offer_id, "needs_review")} className="py-1.5 px-3 rounded-lg text-xs font-semibold border border-amber-300 text-amber-600 hover:bg-amber-50" data-testid={`needs-review-${o.offer_id}`}>
                  <AlertTriangle className="w-3 h-3 inline mr-1" />Review
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* All Offers Tab */}
      {!loading && tab === "all" && (
        <div data-testid="offers-all-table">
          <p className="text-xs text-slate-500 mb-2">{allOffers.total} total offers</p>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b-2 border-slate-200 text-slate-500">
                  <th className="text-left py-2 px-2">Business</th>
                  <th className="text-left py-2 px-2">Offer</th>
                  <th className="text-center py-2 px-2">Type</th>
                  <th className="text-center py-2 px-2">Freebie</th>
                  <th className="text-center py-2 px-2">Confidence</th>
                  <th className="text-center py-2 px-2">Status</th>
                  <th className="text-center py-2 px-2">Staleness</th>
                  <th className="text-center py-2 px-2">By</th>
                </tr>
              </thead>
              <tbody>
                {(allOffers.offers || []).map(o => (
                  <tr key={o.offer_id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-1.5 px-2 text-slate-700">{o.business_id?.slice(0, 12)}</td>
                    <td className="py-1.5 px-2 font-medium text-slate-800 max-w-[150px] truncate">{o.offer_title}</td>
                    <td className="py-1.5 px-2 text-center"><span className="px-1.5 py-0.5 rounded text-[9px] bg-slate-100">{o.offer_type}</span></td>
                    <td className="py-1.5 px-2 text-center">{o.is_true_freebie ? <Check className="w-3 h-3 text-emerald-500 mx-auto" /> : ""}</td>
                    <td className="py-1.5 px-2 text-center font-mono">{(o.confidence_score || 0).toFixed(2)}</td>
                    <td className="py-1.5 px-2 text-center text-[10px]">{o.verification_status}</td>
                    <td className={`py-1.5 px-2 text-center text-[10px] ${stalenessColor[o.staleness_level] || ""}`}>{o.staleness_level}</td>
                    <td className="py-1.5 px-2 text-center text-[10px] text-slate-400">{o.submitted_by}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex justify-between items-center mt-3">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} className="text-xs text-slate-500 disabled:opacity-30">Previous</button>
            <span className="text-xs text-slate-400">Page {page}</span>
            <button onClick={() => setPage(p => p + 1)} disabled={allOffers.offers?.length < 20} className="text-xs text-slate-500 disabled:opacity-30">Next</button>
          </div>
        </div>
      )}

      {/* Type Intelligence Tab */}
      {!loading && tab === "type-intelligence" && typeIntel && (
        <div className="space-y-4" data-testid="offers-type-intelligence">
          {/* Candidates */}
          <div>
            <p className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-2">Pending Candidates</p>
            {(typeIntel.candidates || []).filter(c => c.status === "pending_review").length === 0 && (
              <p className="text-xs text-slate-400">No pending candidates</p>
            )}
            {(typeIntel.candidates || []).filter(c => c.status === "pending_review").map(c => (
              <div key={c.suggested_type_string} className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center justify-between mb-2">
                <div>
                  <p className="text-sm font-bold text-slate-800">{c.suggested_type_string}</p>
                  <p className="text-[10px] text-slate-500">{c.field_name} — {c.signal_count} signals</p>
                </div>
                <div className="flex gap-1">
                  <button onClick={async () => { try { await axios.post(`${API}/birthday-offers/admin/type-candidates/${c.suggested_type_string}/approve`); toast.success("Approved"); fetchTypeIntel(); } catch { toast.error("Failed"); } }}
                    className="px-2 py-1 rounded text-[10px] font-bold bg-emerald-600 text-white" data-testid={`approve-type-${c.suggested_type_string}`}>Approve</button>
                  <button onClick={async () => { try { await axios.post(`${API}/birthday-offers/admin/type-candidates/${c.suggested_type_string}/reject`); toast.success("Rejected"); fetchTypeIntel(); } catch { toast.error("Failed"); } }}
                    className="px-2 py-1 rounded text-[10px] font-bold bg-red-500 text-white" data-testid={`reject-type-${c.suggested_type_string}`}>Reject</button>
                </div>
              </div>
            ))}
          </div>

          {/* Signals */}
          <div>
            <p className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-2">Custom Type Signals</p>
            {(typeIntel.signals || []).length === 0 && <p className="text-xs text-slate-400">No custom signals detected yet</p>}
            <div className="space-y-1">
              {(typeIntel.signals || []).map(s => (
                <div key={`${s.raw_type_string}-${s.field_name}`} className="flex items-center justify-between text-xs bg-slate-50 rounded px-3 py-1.5">
                  <span className="font-mono text-slate-700">{s.raw_type_string}</span>
                  <span className="text-slate-400">{s.field_name} — {s.count}x</span>
                </div>
              ))}
            </div>
          </div>

          {/* Approved */}
          <div>
            <p className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-2">Approved Custom Types</p>
            {(typeIntel.approved || []).length === 0 && <p className="text-xs text-slate-400">No custom types approved yet</p>}
            <div className="flex flex-wrap gap-1">
              {(typeIntel.approved || []).map(a => (
                <span key={a.suggested_type_string} className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-700">{a.suggested_type_string}</span>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
